/* HB 2026-10-09 (Vic, page weight): videos load when they are about to be seen, in the rendition their stage needs.
 *
 * Every Shopify-hosted video in the theme is written by snippets/hb-video.liquid: a <video> with a poster, no
 * <source>, the file Liquid knows in data-hb-src ("url width height") and the HLS playlist in data-hb-hls. Liquid
 * only sees the biggest mp4 (1080p); the renditions Shopify also made (480p, 720p) are listed in the playlist, a
 * 500-byte text file this loader reads once per video and turns into the mp4 addresses (the names follow one
 * pattern: "<id>.HD-720p.4.5Mbps.hls.<n>_.m3u8" is the playlist of "<id>.HD-720p-4.5Mbps-<n>.mp4"). Nothing
 * downloads until the loader gives the element a source, which happens when the video comes within a screen of
 * the viewport (IntersectionObserver). So:
 *   - a copy that is not shown (the hidden phone or desktop layout of the product gallery, the zoom dialog until it
 *     opens, a homepage block hidden on this device, a slide out of view) never loads;
 *   - the rendition follows the stage's width: up to 200 px (a tile, a phone card) 480p, up to 450 px (a phone's
 *     full width: the product gallery, a homepage block) 720p, wider (desktop galleries and blocks) 1080p; the
 *     known 1080p file is always appended as the fallback source, so a rendition that is missing falls through;
 *   - a video added later (recently viewed cards, a swapped card, a re-rendered section, the zoom dialog's clone)
 *     is picked up by a MutationObserver;
 *   - autoplay videos (the "autoplay" attribute) start when they show and pause while out of view.
 * Data saver or a 2G connection: always the smallest rendition.
 *
 * 2026-10-09 evening (Vic's phone test, 20% battery, Low Power Mode, the Silks collection crashing by page 3):
 *   - on phones at most two videos are alive at a time (desktop: four); the ones nearest the viewport win, the
 *     others give their sources back (pause, remove the sources, load()) so the decoder memory is freed, and get
 *     them again when they come near. A video is "near" within half a screen on phones, a full screen on desktop;
 *   - iOS in Low Power Mode refuses autoplay until the first touch: a refused play() is remembered and the first
 *     touch, tap or key press (a scroll counts) starts every autoplay video that is on screen. Until then iOS shows
 *     its own play glyph on the poster frame, kept on purpose (Vic, 21:30 CPH): the shopper sees it is a video.
 *
 * 2026-10-10 (Vic: a quick add, then a fast scroll down a collection, and Safari reloaded the page): the pictures of
 * the product cards are given back too. A card holds up to 14 pictures (seven slides, two of them in a model and a
 * product view, seven colour dots), and the infinite scroll never removes a card, so after a flick through 170 cards
 * the page held 1,400 loaded pictures at 380 px, which is what WebKit's memory limit answers with a reload. Now a
 * card more than two screens from the viewport (phones; three on desktop) hands its pictures' src and srcset to
 * data attributes and gets them back the moment it comes within reach; the browser's lazy loading does the rest, and
 * a picture that was shown once comes back from the cache. The same IntersectionObserver idea as the videos.
 *
 * 2026-10-10, later (Vic: the reload again, flicking while a note was up): the pictures behind a card's first slide
 * (the second view and the lookbook shots, slides 2 and up, four to six per card) are kept back too, from the start
 * and after every return, until she reaches for them: a touch or a press on the card's slideshow or its arrows, a
 * swatch, a swipe (the slideshow scrolling) or, with a mouse, the pointer over the card. A card near the viewport
 * then holds one picture (its first slide, plus the hover view on desktop) instead of up to eight, so a flick through
 * the collection costs a quarter of the memory it did.
 *
 * 2026-10-10, round 28 (the reload measured): the pictures were not the whole story. In this theme every change of
 * an attribute that appears inside a :has() argument (aria-hidden on a slide, open on a dialog, hidden, the header's
 * sticky state) restyles the entire document, because base.css and the compiled styles anchor :has() on body and on
 * the section; with 176 cards (22,000 elements) one such change cost 130 to 270 ms in Chrome on a Mac, and a page of
 * cards added by the infinite scroll (its slideshows write aria-hidden and disabled while they set up) cost about a
 * second, growing with every page. On a phone that is a main thread hung for the length of a flick, which Safari
 * answers by reloading the page. Now a card of the collection grid that is more than two screens away (three on
 * desktop) is not rendered at all: content-visibility: hidden with its measured height kept as the box's intrinsic
 * block size, so nothing moves; its style, layout, paint and compositing are skipped, and a restyle of the document
 * only touches the cards in reach (about 45 on a phone). A page the infinite scroll adds beyond the margin is skipped
 * from the moment it lands, before its slideshows do their layout reads. Pictures are still given back as before
 * (browsers without content-visibility keep that much). The cards come back the moment they are within reach.
 *
 * 2026-10-22 (Vic, the front page on a phone fetched about 14 MB of video as it opened: the hero, 8.3 MB, and the
 * next video, 5.9 MB, a screen and a half down, paused but fetched in full at the same time): a video below or
 * above the screen gets its source only once every autoplay video on screen can play through (the browser's
 * canplaythrough), so the one she is looking at has the line to itself. A video that comes on screen is attached at
 * once, as before. If the one on screen is not ready after 8 s (a slow line, Low Power Mode refusing autoplay, an
 * error), the others are not held back any longer.
 *
 * 2026-10-23, round 89 (Vic: "some lag/flicker on video", a screen recording of the front page on his phone): the cap
 * of two was meant for the product cards (round 28: the Silks collection crashing by page 3), but it counted the big
 * section videos too. On the front page the hero, the Swimsuits video and the carousel's video took turns: scrolled
 * past, the hero gave its source back, and scrolled to again it fetched and decoded its 8 MB from the start, a blank
 * moment each time. Now the section videos (any video not inside a product card) have places of their own: three on
 * phones, six on desktop, the nearest kept. One scrolled past keeps its source, paused, and plays on at once when she
 * comes back; only beyond those places does the farthest give its source back. The cards keep their cap of two (four).
 *
 * Round 90 (the Our Silk Tulle GIFs became videos: an open item shows four at once on a phone): a section video on
 * screen always gets its source, even past its places; the places only decide which ones off screen keep theirs.
 *
 * Round 91 (Vic, Our Silk Tulle on his iPhone: "a small blink, i.e. brief white flash before the video loads and
 * plays"; his screen recording shows an opened item's posters for a moment, then white for about a third of a second,
 * then the moving video): Safari takes the poster away as soon as the video is given its source and paints nothing
 * until the first frame is decoded. The poster is now also the video's own background, sized and placed as the video
 * is (its object-fit and object-position), so whatever the player is doing, the picture underneath is the poster and
 * the poster turns into the moving video without a white moment. Also: a video with no box (inside a closed item in
 * Safari, a closed dialog, a copy hidden on this device) counted as on screen (its empty rectangle sits at the top of
 * the viewport), so it kept its source and was told to play while hidden, and Chrome, which lays out a closed item's
 * content without showing it, gave the videos of every closed item their sources as the page opened. A video with no
 * box or inside a closed item now counts as far away: it gets no source, one it has is paused and is the first to be
 * given back when places run out, and it is attached the moment the item opens (the item's toggle).
 */
(function () {
  'use strict';
  if (typeof window === 'undefined' || !document.querySelector) return;

  var SEL = 'video[data-hb-src]';
  var conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection || null;
  var playlists = {};
  var PHONE = (window.matchMedia && window.matchMedia('(max-width: 749px)').matches) || (navigator.maxTouchPoints > 1 && window.innerWidth < 1000);
  var MAX_ALIVE = PHONE ? 2 : 4; // the product cards' videos
  var MAX_SECTION = PHONE ? 3 : 6; // round 89: the section videos (the hero, a block's video), places of their own
  // HB 2026-10-12 (round 30): a video ahead of her (below) gets its source a screen and a half early, so it is playing
  // when she arrives; behind her, as before. The cap (two alive on phones, four on desktop) is unchanged.
  var NEAR = PHONE ? '50% 0px 150% 0px' : '100% 0px 150% 0px';
  // HB 2026-10-22: how long a video on screen may hold back the ones further away while it is not ready to play through
  var READY_WAIT = 8000;
  var alive = []; // videos that currently hold a source, nearest first after each reconcile
  var wanted = []; // videos within the near margin, as the observer reports them
  var blocked = false; // autoplay refused (Low Power Mode): waiting for a user gesture

  function parseList(raw) {
    var list = [];
    String(raw || '').split(',').forEach(function (part) {
      var bits = part.trim().split(/\s+/);
      if (bits.length < 3) return;
      var w = parseInt(bits[1], 10);
      var h = parseInt(bits[2], 10);
      if (!bits[0] || !(w > 0)) return;
      list.push({ url: bits[0], w: w, h: h });
    });
    return list;
  }

  // The playlist's variants, as mp4 addresses next to the playlist.
  function fromPlaylist(text, hlsUrl) {
    var out = [];
    var dir = hlsUrl.substring(0, hlsUrl.lastIndexOf('/') + 1);
    var lines = String(text || '').split(/\r?\n/);
    var pending = null;
    for (var i = 0; i < lines.length; i++) {
      var line = lines[i].trim();
      if (!line) continue;
      if (line.indexOf('#EXT-X-STREAM-INF') === 0) {
        var m = /RESOLUTION=(\d+)x(\d+)/.exec(line);
        pending = m ? { w: parseInt(m[1], 10), h: parseInt(m[2], 10) } : null;
        continue;
      }
      if (line.charAt(0) === '#') continue;
      if (pending) {
        var name = /^(.+?)\.([A-Za-z]+-\d+p)\.([\d.]+Mbps)\.hls\.(\d+)_?\.m3u8$/.exec(line.replace(/\?.*$/, ''));
        if (name && pending.w > 0) out.push({ url: dir + name[1] + '.' + name[2] + '-' + name[3] + '-' + name[4] + '.mp4', w: pending.w, h: pending.h });
        pending = null;
      }
    }
    return out;
  }

  function renditions(video) {
    var base = parseList(video.getAttribute('data-hb-src'));
    var hls = video.getAttribute('data-hb-hls');
    if (!hls || !window.fetch) return Promise.resolve(base);
    if (!playlists[hls]) {
      playlists[hls] = fetch(hls, { credentials: 'omit' })
        .then(function (r) { return r.ok ? r.text() : ''; })
        .then(function (text) {
          var found = fromPlaylist(text, hls);
          // the file Liquid knows stays in the list (it is the fallback); the playlist adds the smaller ones
          // A phone recording is often stored on its side and turned by the player: Liquid may say 1080x1920 where
          // the playlist says 1920x1080 for the same file, so a rendition is the same when its two sides match.
          var all = base.slice();
          found.forEach(function (f) {
            var dup = false;
            for (var i = 0; i < all.length; i++) {
              if (Math.max(all[i].w, all[i].h) === Math.max(f.w, f.h) && Math.min(all[i].w, all[i].h) === Math.min(f.w, f.h)) dup = true;
            }
            if (!dup) all.push(f);
          });
          return all;
        })
        .catch(function () { return base; });
    }
    return playlists[hls];
  }

  function stageWidth(video) {
    var w = video.getBoundingClientRect().width;
    if (!w && video.parentElement) w = video.parentElement.getBoundingClientRect().width;
    if (!w) w = Math.min(window.innerWidth || 0, 750);
    return w;
  }

  // Three tiers by the stage's width in CSS pixels: a tile or card up to 200 px plays 480p, anything up to 450 px
  // (a phone's full width) 720p, wider stages 1080p. A rendition is known by the "p" number in its file name
  // (SD-480p, HD-720p, HD-1080p), the same whichever way the recording is stored.
  function pOf(r) {
    var m = /[-.](\d{3,4})p[-.]/.exec(r.url || '');
    return m ? parseInt(m[1], 10) : Math.min(r.w, r.h);
  }

  function tier(video) {
    var thrifty = conn && (conn.saveData || /(^|-)2g$/.test(String(conn.effectiveType || '')));
    if (thrifty) return 480;
    var w = stageWidth(video);
    if (w <= 200) return 480;
    if (w <= 450) return 720;
    return 1080;
  }

  function choose(video, list) {
    if (!list.length) return null;
    var want = tier(video);
    var sorted = list.map(function (r) { return { url: r.url, w: r.w, h: r.h, p: pOf(r) }; });
    sorted.sort(function (a, b) { return a.p - b.p; });
    for (var i = 0; i < sorted.length; i++) {
      if (sorted[i].p >= want) return sorted[i];
    }
    return sorted[sorted.length - 1];
  }

  // Round 91: the poster as the video's background, so Safari's empty moment between the poster and the first frame
  // shows the poster, not the page. Placed as the video is (object-fit, object-position); a background set by the
  // theme or the section is left alone.
  function backdrop(video) {
    var url = video.getAttribute('poster');
    if (!url || video.style.backgroundImage) return;
    var fit = 'contain';
    var pos = '50% 50%';
    try {
      var cs = window.getComputedStyle(video);
      if (cs.backgroundImage && cs.backgroundImage !== 'none') return;
      fit = cs.objectFit || fit;
      pos = cs.objectPosition || pos;
    } catch (e) { /* no-op */ }
    video.style.backgroundImage = 'url("' + url.replace(/["\\]/g, '\\$&') + '")';
    video.style.backgroundRepeat = 'no-repeat';
    video.style.backgroundPosition = pos;
    video.style.backgroundSize = fit === 'cover' ? 'cover' : fit === 'fill' ? '100% 100%' : fit === 'none' ? 'auto' : 'contain';
  }

  function addSource(video, url) {
    var source = document.createElement('source');
    source.src = url;
    source.type = 'video/mp4';
    source.setAttribute('data-hb', '1');
    video.appendChild(source);
  }

  function attach(video) {
    if (video.__hbSrc || video.__hbBusy) return;
    video.__hbBusy = true;
    renditions(video).then(function (list) {
      video.__hbBusy = false;
      if (video.__hbSrc || !video.isConnected) return;
      if (alive.indexOf(video) === -1) alive.push(video);
      var r = choose(video, list);
      if (!r) return;
      var base = parseList(video.getAttribute('data-hb-src'));
      var fallback = base.length ? base[base.length - 1].url : null;
      video.__hbSrc = r.url;
      video.__hbFallback = fallback && fallback !== r.url ? fallback : null;
      video.__hbAt = Date.now();
      video.__hbReady = false;
      if (!video.__hbReadyWatch) {
        video.__hbReadyWatch = true;
        var onReady = function () { video.__hbReady = true; scheduleReconcile(); };
        video.addEventListener('canplaythrough', onReady);
        video.addEventListener('error', onReady, true);
      }
      var lazyPoster = video.getAttribute('data-hb-poster');
      if (lazyPoster && !video.getAttribute('poster')) video.setAttribute('poster', lazyPoster);
      backdrop(video);
      addSource(video, r.url);
      if (video.__hbFallback) addSource(video, video.__hbFallback);
      if (video.hasAttribute('autoplay')) video.muted = true;
      if (video.getAttribute('preload') === 'none') video.setAttribute('preload', 'metadata');
      try { video.load(); } catch (e) { /* no-op */ }
      play(video);
      if (video.__hbWantsPlay) { video.__hbWantsPlay = false; var p = video.play(); if (p && typeof p.catch === 'function') p.catch(function () { /* no-op */ }); }
      scheduleReconcile();
    });
  }

  function play(video) {
    if (!video.hasAttribute('autoplay') || !video.__hbSrc) return;
    if (!video.paused) return;
    video.muted = true;
    var p = video.play();
    if (p && typeof p.catch === 'function') p.catch(function (err) {
      // NotAllowedError: autoplay refused (Low Power Mode, or no gesture yet); a gesture retries it
      if (err && err.name === 'NotAllowedError') blocked = true;
    });
  }

  function pause(video) {
    if (!video.hasAttribute('autoplay')) return;
    if (!video.paused) {
      try { video.pause(); } catch (e) { /* no-op */ }
    }
  }

  // Gives the sources back so the decoder is released; the video is attached again when it comes near.
  function detach(video) {
    var i = alive.indexOf(video);
    if (i !== -1) alive.splice(i, 1);
    if (!video.__hbSrc) return;
    pause(video);
    var sources = video.querySelectorAll('source[data-hb]');
    for (var k = 0; k < sources.length; k++) sources[k].parentNode.removeChild(sources[k]);
    video.__hbSrc = null;
    video.__hbFallback = null;
    video.removeAttribute('src');
    try { video.load(); } catch (e) { /* no-op */ }
  }

  // Distance from the viewport's centre, in viewport heights; 0 while on screen. Round 91: FAR for a video that is not
  // shown at all (no box, or inside a closed item).
  var FAR = 1e6;
  function distance(video) {
    var r = video.getBoundingClientRect();
    if (!r.width && !r.height) return FAR;
    if (video.closest && video.closest('details:not([open])')) return FAR;
    var vh = window.innerHeight || 1;
    if (r.bottom >= 0 && r.top <= vh) return 0;
    return (r.top > vh ? r.top - vh : -r.bottom) / vh;
  }

  // round 89: a video inside a product card, or a section's own (the hero, a block's video)
  function inCard(v) {
    if (v.__hbCard === undefined) v.__hbCard = !!(v.closest && v.closest('product-card'));
    return v.__hbCard;
  }
  function cap(v) {
    return inCard(v) ? MAX_ALIVE : MAX_SECTION;
  }
  function pool(v) {
    var n = 0;
    for (var k = 0; k < alive.length; k++) if (inCard(alive[k]) === inCard(v)) n++;
    return n;
  }

  // The nearest wanted videos get the places (the cards' MAX_ALIVE, the sections' MAX_SECTION); a card video that is not
  // among them gives its sources back. A section video keeps its source while its places allow (round 89), nearest first.
  var reconcileTimer = 0;
  function reconcile() {
    reconcileTimer = 0;
    var i;
    for (i = wanted.length - 1; i >= 0; i--) if (!wanted[i].isConnected) wanted.splice(i, 1);
    for (i = alive.length - 1; i >= 0; i--) if (!alive[i].isConnected) alive.splice(i, 1);
    var ranked = wanted.slice().sort(function (a, b) { return distance(a) - distance(b); });
    var keep = [];
    var nCard = 0;
    var nSec = 0;
    for (i = 0; i < ranked.length; i++) {
      if (distance(ranked[i]) >= FAR) continue; // round 91: not shown (a closed item): no source
      if (inCard(ranked[i])) {
        if (nCard < MAX_ALIVE) { keep.push(ranked[i]); nCard++; }
      } else if (nSec < MAX_SECTION || distance(ranked[i]) === 0) { keep.push(ranked[i]); nSec++; } // round 90: on screen
    }
    var byNear = alive.slice().sort(function (a, b) { return distance(a) - distance(b); });
    var secLeft = MAX_SECTION - nSec;
    for (i = 0; i < byNear.length; i++) {
      var v = byNear[i];
      if (keep.indexOf(v) !== -1 || distance(v) === 0) continue;
      if (!inCard(v) && secLeft > 0) { secLeft--; continue; } // round 89: paused, its source kept
      detach(v);
    }
    var held = false;
    for (i = 0; i < keep.length; i++) {
      if (keep[i].__hbSrc || keep[i].__hbBusy) continue;
      // HB 2026-10-22: off screen, it waits until the videos on screen can play through
      if (distance(keep[i]) > 0 && !screenReady()) { held = true; continue; }
      if (pool(keep[i]) >= cap(keep[i])) {
        if (inCard(keep[i])) continue;
        // a section video's place: the farthest section video kept from before gives way
        var far = null;
        for (var f = byNear.length - 1; f >= 0; f--) {
          if (!inCard(byNear[f]) && byNear[f].__hbSrc && keep.indexOf(byNear[f]) === -1 && distance(byNear[f]) > 0) { far = byNear[f]; break; }
        }
        if (far) detach(far);
        else if (distance(keep[i]) > 0) continue; // round 90: on screen it is attached all the same
      }
      attach(keep[i]);
    }
    if (held && !gateTimer) gateTimer = setTimeout(function () { gateTimer = 0; scheduleReconcile(); }, 1000);
    for (i = 0; i < alive.length; i++) {
      if (distance(alive[i]) === 0) play(alive[i]);
      else pause(alive[i]);
    }
  }
  // HB 2026-10-22: every autoplay video on screen has its source and can play through. One still being attached counts
  // as not ready; the wait never lasts longer than READY_WAIT, whatever holds it (a slow line, a missing file).
  var gateTimer = 0;
  var gateSince = 0;
  function isReady(v) {
    if (v.__hbReady || blocked || v.error) return true;
    if (v.readyState >= 4) { v.__hbReady = true; return true; }
    if (v.__hbAt && Date.now() - v.__hbAt > READY_WAIT) { v.__hbReady = true; return true; }
    return false;
  }
  function screenReady() {
    var ok = true;
    for (var k = 0; k < wanted.length; k++) {
      var v = wanted[k];
      if (!v.isConnected || !v.hasAttribute('autoplay') || distance(v) > 0) continue;
      if (!v.__hbSrc || !isReady(v)) { ok = false; break; }
    }
    if (ok) { gateSince = 0; return true; }
    if (!gateSince) gateSince = Date.now();
    return Date.now() - gateSince > READY_WAIT;
  }
  function scheduleReconcile() {
    if (reconcileTimer) return;
    reconcileTimer = setTimeout(reconcile, 120);
  }

  var io = null;
  if ('IntersectionObserver' in window) {
    io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var video = entry.target;
        var at = wanted.indexOf(video);
        if (entry.isIntersecting) {
          if (at === -1) wanted.push(video);
        } else {
          if (at !== -1) wanted.splice(at, 1);
          if (video.__hbSrc) pause(video);
        }
      });
      scheduleReconcile();
    }, { rootMargin: NEAR, threshold: 0 });
    window.addEventListener('scroll', scheduleReconcile, { passive: true });
    window.addEventListener('resize', scheduleReconcile, { passive: true });
    // Round 91: an item opens or closes (a <details>; its toggle does not bubble, so it is caught on the way down): its
    // videos are sorted out at once, not after the scroll's pause.
    document.addEventListener('toggle', function (e) {
      var t = e.target;
      if (!t || !t.querySelector || !t.querySelector(SEL)) return;
      clearTimeout(reconcileTimer);
      reconcileTimer = setTimeout(reconcile, 0);
    }, true);
  }

  // Low Power Mode (iOS) refuses autoplay until a user gesture: the first touch starts what is on screen.
  function onGesture() {
    // every touch or key press, not only the first: in Low Power Mode each video may need its own gesture.
    // Any autoplay video on screen, the loader's or an old-style one with its own <source>.
    blocked = false;
    var all = document.querySelectorAll('video[autoplay]:not([controls])');
    for (var i = 0; i < all.length; i++) {
      var v = all[i];
      if (!v.paused || distance(v) !== 0) continue;
      if (!v.__hbSrc && !v.currentSrc && !v.querySelector('source')) continue;
      v.muted = true;
      var p = v.play();
      if (p && typeof p.catch === 'function') p.catch(function () { blocked = true; });
    }
  }
  ['touchstart', 'touchend', 'pointerdown', 'mousedown', 'keydown'].forEach(function (type) {
    document.addEventListener(type, onGesture, { passive: true, capture: true });
  });

  function watch(video) {
    if (video.__hbWatched) return;
    video.__hbWatched = true;
    // A poster held back for a device-specific copy (data-hb-poster) is set at once when the copy is laid out on
    // this device, so a block on the first screen shows its frame without waiting for the observer.
    var held = video.getAttribute('data-hb-poster');
    if (held && !video.getAttribute('poster') && video.getBoundingClientRect().width > 0) video.setAttribute('poster', held);
    // A tap on the video (or a play from the theme's own buttons) always gets a source, whatever the observer saw.
    video.addEventListener('pointerdown', function () { if (wanted.indexOf(video) === -1) wanted.push(video); attach(video); }, true);
    video.addEventListener('play', function () {
      if (video.__hbSrc) return;
      // The theme's own scripts call play() on every card video at load; only one that is near the viewport
      // gets a source for it (the cap would be meaningless otherwise). A far one is picked up by the observer
      // when it comes near.
      if (distance(video) > (PHONE ? 0.5 : 1)) {
        try { video.pause(); } catch (e) { /* no-op */ }
        return;
      }
      video.__hbWantsPlay = true; // the play she asked for is repeated once the source is in
      if (wanted.indexOf(video) === -1) wanted.push(video);
      attach(video);
    }, true);
    if (io) io.observe(video);
    else attach(video);
  }

  /* ---------- the cards' pictures: put away when far, given back and fetched ahead when near (2026-10-10, 2026-10-12) ---------- */
  var CARD_SEL = 'product-card';
  /* HB 2026-10-12 (round 30, Vic on the live site: "I repeatedly had to wait a brief moment for product images to load
     when I scrolled down a collection page. It seemed that very little was pre-loaded."). Until today one margin did
     two jobs: within two screens (three on desktop) a card was rendered and held its pictures, and a picture given back
     still waited for the browser's own lazy loading, which on an iPhone starts only just before the picture is on
     screen. Now two margins:
       RENDER (two screens each way on phones, three on desktop, as round 28): which cards of the grid are rendered;
       WARM (two screens up and five down on phones; three up and six down on desktop): which cards hold their pictures.
     A card that comes within WARM has its first picture, its colour dots and its quick add picture fetched at once
     (loading="eager"), so the cards she is scrolling towards are loaded three to five screens before she gets there.
     A picture fetched for a card that is not rendered yet is not decoded, so the memory limit of round 28 holds; the
     cards far behind her still give their pictures back (the grid's cards only: a carousel's few cards keep theirs).
     The fetching ahead starts once the page itself has loaded, never on Data Saver or 2G, and the deep slides (2 and
     up) still wait until she reaches for the card. */
  var RENDER = PHONE ? '200% 0px 200% 0px' : '300% 0px 300% 0px';
  var WARM = PHONE ? '200% 0px 500% 0px' : '300% 0px 600% 0px';
  var cardIo = null; // WARM: the pictures
  var renderIo = null; // RENDER: the grid's cards rendered or skipped
  var SLOW_NET = !!(conn && (conn.saveData || /(^|-)2g$/.test(String(conn.effectiveType || ''))));

  // The pictures behind the first slide (slides 2 and up) are "deep": kept back until she reaches for them (wake).
  var DEEP_SLIDE = 'slideshow-slide:nth-child(n+2)';

  function isDeep(img) {
    return !!(img.closest && img.closest(DEEP_SLIDE));
  }

  function putAway(img) {
    var src = img.getAttribute('src');
    var srcset = img.getAttribute('srcset');
    if (srcset) { img.setAttribute('data-hb-shed-srcset', srcset); img.removeAttribute('srcset'); }
    if (src) { img.setAttribute('data-hb-shed-src', src); img.removeAttribute('src'); }
  }

  function giveBack(img) {
    var srcset = img.getAttribute('data-hb-shed-srcset');
    var src = img.getAttribute('data-hb-shed-src');
    if (srcset) { img.setAttribute('srcset', srcset); img.removeAttribute('data-hb-shed-srcset'); }
    if (src) { img.setAttribute('src', src); img.removeAttribute('data-hb-shed-src'); }
  }

  /* HB 2026-10-10 (round 28): a far card of the collection grid is not rendered at all. content-visibility: hidden
     skips its style, layout, paint and compositing; the box keeps the height it had (contain-intrinsic-block-size),
     so the grid and the scroll position do not move. Only the grid's cards (the infinite scroll's pages), never the
     cards of a carousel (recently viewed, recommendations), where a card just off the edge would pop in on a swipe. */
  var SKIP_OK = (function () {
    try {
      return !!(window.CSS && CSS.supports && CSS.supports('content-visibility', 'hidden') && CSS.supports('contain-intrinsic-block-size', '1px'));
    } catch (e) { return false; }
  })();
  var GRID_ITEM = '[ref="cards[]"], .product-grid__item';
  var FAR_SCREENS = PHONE ? 2 : 3; // the same margin as RENDER, for a card that has just been added (the observer is not there yet)

  function inGrid(card) {
    return !!(card.closest && card.closest(GRID_ITEM));
  }

  // The card is beyond the margin right now (read: forces a layout, so call it in a read phase).
  function farNow(card) {
    var r = card.getBoundingClientRect();
    var vh = window.innerHeight || 1;
    return r.bottom < -FAR_SCREENS * vh || r.top > vh + FAR_SCREENS * vh;
  }

  // Write phase: the card keeps a box of height h and renders nothing.
  function skip(card, h) {
    if (!SKIP_OK || card.__hbSkipped || !inGrid(card)) return;
    if (!(h > 0)) return; // no layout to remember (a hidden container): left as it is
    card.style.containIntrinsicBlockSize = h + 'px';
    card.style.contentVisibility = 'hidden';
    card.__hbSkipped = true;
  }

  function unskip(card) {
    if (!card.__hbSkipped) return;
    card.style.contentVisibility = '';
    card.style.containIntrinsicBlockSize = '';
    card.__hbSkipped = false;
  }

  // Far beyond WARM: every picture of the card is put away (rendering is RENDER's business, see renderIo).
  function shed(card) {
    if (card.__hbShed) return;
    var imgs = card.querySelectorAll('img');
    for (var i = 0; i < imgs.length; i++) putAway(imgs[i]);
    card.__hbShed = true;
  }

  // Fetched now, not when the browser's lazy loading gets to them. Before the page's own load event the cards wait in
  // a queue, so the first screen's pictures and the page's scripts are not kept waiting.
  var warmOpen = false;
  var warmQueue = [];
  function warm(card) {
    if (SLOW_NET) return;
    if (!warmOpen) {
      if (warmQueue.indexOf(card) === -1) warmQueue.push(card);
      return;
    }
    var imgs = card.querySelectorAll('img[loading="lazy"]');
    for (var i = 0; i < imgs.length; i++) {
      var img = imgs[i];
      if (!card.__hbAwake && isDeep(img)) continue;
      if (!img.getAttribute('src') && !img.getAttribute('srcset')) continue; // put away: comes back with dress()
      img.loading = 'eager';
    }
  }
  function openWarm() {
    if (warmOpen) return;
    warmOpen = true;
    var q = warmQueue;
    warmQueue = [];
    for (var i = 0; i < q.length; i++) if (q[i].isConnected && !q[i].__hbShed) warm(q[i]);
  }
  if (document.readyState === 'complete') setTimeout(openWarm, 0);
  else {
    window.addEventListener('load', function () { setTimeout(openWarm, 0); });
    setTimeout(openWarm, 5000); // a page whose load event is held up by a slow third party
  }

  // Within WARM: the first slide, the dots and the quick add's picture come back and are fetched at once; the deep
  // slides only once she has reached for them (card.__hbAwake).
  function dress(card) {
    if (card.__hbShed) {
      var imgs = card.querySelectorAll('img[data-hb-shed-src], img[data-hb-shed-srcset]');
      for (var i = 0; i < imgs.length; i++) {
        if (!card.__hbAwake && isDeep(imgs[i])) continue;
        giveBack(imgs[i]);
      }
      card.__hbShed = false;
    }
    warm(card);
  }

  // The widths changed (a turn of the phone, a resized window): the skipped cards are shown for one layout, measured
  // again and skipped again with their new heights.
  var remeasureTimer = 0;
  function remeasure() {
    remeasureTimer = 0;
    var all = document.querySelectorAll(CARD_SEL);
    var list = [];
    var i;
    for (i = 0; i < all.length; i++) {
      if (!all[i].__hbSkipped) continue;
      unskip(all[i]);
      list.push(all[i]);
    }
    if (!list.length) return;
    var hs = [];
    for (i = 0; i < list.length; i++) hs.push(list[i].offsetHeight);
    for (i = 0; i < list.length; i++) skip(list[i], hs[i]);
  }
  if (SKIP_OK) {
    window.addEventListener('resize', function () {
      clearTimeout(remeasureTimer);
      remeasureTimer = setTimeout(remeasure, 250);
    }, { passive: true });
  }

  // From the start (the page's own HTML carries every slide's picture): the deep slides are put away as the card is
  // first seen by this script, before most of them have begun to load.
  function shedDeep(card) {
    if (card.__hbAwake) return;
    var imgs = card.querySelectorAll(DEEP_SLIDE + ' img');
    for (var i = 0; i < imgs.length; i++) putAway(imgs[i]);
  }

  // She reaches for the slides: a touch or a press on the slideshow or its arrows, a swatch, a swipe, a hover. The deep
  // slides come back and are fetched at once (she is about to swipe to them).
  function wake(card) {
    if (!card || card.__hbAwake) return;
    card.__hbAwake = true;
    if (card.__hbShed) return; // far away: dress() gives everything back when it comes near
    var imgs = card.querySelectorAll(DEEP_SLIDE + ' img[data-hb-shed-src], ' + DEEP_SLIDE + ' img[data-hb-shed-srcset]');
    for (var i = 0; i < imgs.length; i++) giveBack(imgs[i]);
    if (!SLOW_NET) {
      var deep = card.querySelectorAll(DEEP_SLIDE + ' img[loading="lazy"]');
      for (var j = 0; j < deep.length; j++) deep[j].loading = 'eager';
    }
  }

  function cardOf(target) {
    return target && target.closest ? target.closest(CARD_SEL) : null;
  }

  function onReach(e) {
    var t = e.target;
    if (!t || !t.closest) return;
    if (!t.closest('slideshow-component, slideshow-slides, [class*="swatch"]')) return;
    wake(cardOf(t));
  }

  if ('IntersectionObserver' in window) {
    cardIo = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        var card = entries[i].target;
        if (entries[i].isIntersecting) dress(card);
        else if (inGrid(card)) shed(card);
      }
    }, { rootMargin: WARM, threshold: 0 });
    if (SKIP_OK) {
      renderIo = new IntersectionObserver(function (entries) {
        // Reads first (the far cards' heights), then the writes, so the batch costs one layout.
        var near = [];
        var far = [];
        var i;
        for (i = 0; i < entries.length; i++) (entries[i].isIntersecting ? near : far).push(entries[i].target);
        var hs = [];
        for (i = 0; i < far.length; i++) hs.push(far[i].__hbSkipped ? 0 : far[i].offsetHeight);
        for (i = 0; i < near.length; i++) unskip(near[i]);
        for (i = 0; i < far.length; i++) skip(far[i], hs[i]);
      }, { rootMargin: RENDER, threshold: 0 });
    }
    document.addEventListener('touchstart', onReach, { capture: true, passive: true });
    document.addEventListener('pointerdown', onReach, { capture: true, passive: true });
    // A swipe or the arrows scroll the slideshow's own scroller (scroll does not bubble; it is seen in the capture phase).
    document.addEventListener('scroll', function (e) {
      var t = e.target;
      if (t && t.tagName && t.tagName.toLowerCase() === 'slideshow-slides') wake(cardOf(t));
    }, { capture: true, passive: true });
    if (window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
      document.addEventListener('pointerover', function (e) { wake(cardOf(e.target)); }, { capture: true, passive: true });
    }
  }

  function watchCard(card) {
    if (card.__hbCardWatched || !cardIo) return;
    card.__hbCardWatched = true;
    shedDeep(card);
    cardIo.observe(card);
    if (renderIo && inGrid(card)) renderIo.observe(card);
  }

  // The cards under root that this script has not seen yet, each put under the observer.
  function scanCards(root) {
    var list = [];
    if (!cardIo || !root || !root.querySelectorAll) return list;
    if (root.matches && root.matches(CARD_SEL) && !root.__hbCardWatched) list.push(root);
    var found = root.querySelectorAll(CARD_SEL);
    var i;
    for (i = 0; i < found.length; i++) if (!found[i].__hbCardWatched) list.push(found[i]);
    for (i = 0; i < list.length; i++) watchCard(list[i]);
    return list;
  }

  // A page the infinite scroll has just added beyond RENDER is skipped at once, before its slideshows get to their
  // layout reads (the observers' first reports come a frame later, after that work has been done). One read phase for
  // the whole batch, then the writes. Its pictures are left as they came: the page lands within WARM, and the WARM
  // observer's first report fetches them.
  function settleFresh(list) {
    if (!SKIP_OK || !list.length) return;
    var fresh = [];
    var i;
    for (i = 0; i < list.length; i++) {
      if (!list[i].__hbSkipped && list[i].isConnected && inGrid(list[i])) fresh.push(list[i]);
    }
    if (!fresh.length) return;
    var far = [];
    var hs = [];
    for (i = 0; i < fresh.length; i++) {
      if (!farNow(fresh[i])) continue;
      far.push(fresh[i]);
      hs.push(fresh[i].offsetHeight);
    }
    for (i = 0; i < far.length; i++) skip(far[i], hs[i]);
  }

  /* ---------- the product page: what is further down is ready while she reads (2026-10-12, round 30) ---------- */
  /* Vic: "If I have been on a PDP for a while, then anything on there should be ready and waiting once I decide to
     scroll down." Two and a half seconds after the product page has loaded, the pictures further down that are still
     waiting for the browser's lazy loading (the gallery's other views, the looks, the recently viewed cards, the
     Instagram tiles) are fetched, four at a time, in page order, and so are the ones that arrive later (the recently
     viewed cards). Not on Data Saver or 2G; not a picture in a hidden copy of a layout (the phone or desktop gallery
     that is not shown); not the deep slides of a card. A GIF (they run to several MB) comes last, one at a time, once
     every other picture is in, so it never holds the others back (2026-10-12b). */
  var pdpOn = false;
  var pdpBusy = 0;
  var pdpTimer = 0;
  function isPdp() {
    return !!(document.body && /(^|\s)page_template_product(\s|$)/.test(document.body.className));
  }
  function pdpNext() {
    var all = document.querySelectorAll('img[loading="lazy"]');
    var out = [];
    var gif = null;
    for (var i = 0; i < all.length && out.length < 4 - pdpBusy; i++) {
      var img = all[i];
      if (img.__hbWarmed) continue;
      var src = img.getAttribute('src') || '';
      if (!src && !img.getAttribute('srcset')) continue; // put away, or not set yet
      var card = img.closest ? img.closest(CARD_SEL) : null;
      if (card && !card.__hbAwake && isDeep(img)) continue;
      if (!img.getClientRects().length) continue; // in a hidden copy (display: none)
      if (img.complete && img.naturalWidth > 0) { img.__hbWarmed = true; continue; }
      if (/\.gif(\?|$)/i.test(src)) { if (!gif) gif = img; continue; }
      out.push(img);
    }
    if (!out.length && !pdpBusy && gif) out.push(gif); // the GIFs last, one at a time
    return out;
  }
  function pdpStep() {
    pdpTimer = 0;
    var list = pdpNext();
    list.forEach(function (img) {
      var settled = false;
      img.__hbWarmed = true;
      pdpBusy++;
      function done() {
        if (settled) return;
        settled = true;
        img.removeEventListener('load', done);
        img.removeEventListener('error', done);
        pdpBusy--;
        pdpKick(0);
      }
      img.addEventListener('load', done);
      img.addEventListener('error', done);
      setTimeout(done, 8000); // a picture that never answers does not hold its place in the four
      img.loading = 'eager';
      if (img.complete) done();
    });
  }
  function pdpKick(ms) {
    if (!pdpOn || pdpTimer) return;
    pdpTimer = setTimeout(pdpStep, ms);
  }
  function startPdp() {
    if (pdpOn || SLOW_NET || !isPdp()) return;
    pdpOn = true;
    pdpKick(0);
  }
  if (document.readyState === 'complete') setTimeout(startPdp, 2500);
  else window.addEventListener('load', function () { setTimeout(startPdp, 2500); });


  function scanVideos(root) {
    if (!root || !root.querySelectorAll) return;
    if (root.matches && root.matches(SEL)) watch(root);
    var found = root.querySelectorAll(SEL);
    for (var i = 0; i < found.length; i++) watch(found[i]);
  }

  function scan(root) {
    scanVideos(root);
    settleFresh(scanCards(root));
  }

  // Several roots at once (the mutation observer's batch): the cards of all of them share one read phase.
  function scanMany(roots) {
    var cards = [];
    for (var i = 0; i < roots.length; i++) {
      scanVideos(roots[i]);
      cards = cards.concat(scanCards(roots[i]));
    }
    settleFresh(cards);
  }

  // A section re-render (the product form morphs the page on a variant change) rebuilds children from the server's
  // HTML, which has no <source>: a video that had one gets it back.
  function restore(video) {
    if (!video.__hbSrc || !video.isConnected) return;
    if (video.querySelector('source[data-hb]')) return;
    backdrop(video);
    addSource(video, video.__hbSrc);
    if (video.__hbFallback) addSource(video, video.__hbFallback);
    try { video.load(); } catch (e) { /* no-op */ }
    play(video);
  }

  function start() {
    scan(document);
    if (!('MutationObserver' in window)) return;
    var mo = new MutationObserver(function (records) {
      var added = [];
      for (var i = 0; i < records.length; i++) {
        var rec = records[i];
        for (var j = 0; j < rec.addedNodes.length; j++) {
          var node = rec.addedNodes[j];
          if (node.nodeType === 1) added.push(node);
        }
        if (rec.removedNodes.length && rec.target && rec.target.tagName === 'VIDEO' && rec.target.__hbSrc) {
          restore(rec.target);
        }
      }
      if (added.length) {
        scanMany(added);
        pdpKick(300);
      }
    });
    mo.observe(document.documentElement, { childList: true, subtree: true });
  }

  // For a test in the console: HBVideo.renditions(video) lists what a video can get, HBVideo.attach(video) gives it.
  window.HBVideo = { version: '2026-10-23r91', maxSection: MAX_SECTION, renditions: renditions, choose: choose, attach: attach, detach: detach, scan: scan, fromPlaylist: fromPlaylist, alive: function () { return alive.slice(); }, wanted: function () { return wanted.slice(); }, reconcile: reconcile, phone: PHONE, shed: shed, dress: dress, wake: wake, skip: skip, unskip: unskip, remeasure: remeasure, skipOk: SKIP_OK, warm: warm, warmOpen: function () { return warmOpen; }, pdpWarm: function () { return { on: pdpOn, busy: pdpBusy }; }, screenReady: function () { return screenReady(); } };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
